import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { ENTITY_DOC_FILE_STATUS, PERMISSIONS } from "../../constants";
import {
  DOC_FILE_STATUS_COLOR,
  DOC_FILE_STATUS_LABELS,
  ENTITY_REQUEST_ERROR_LABELS,
  formatBytes,
  formatDate,
  formatDateTime,
} from "./entityLabels";
import {
  catalogForEntityType,
  daysUntilExpiry,
  docFiles,
  fileEffectiveStatus,
  isoToday,
  validateIssueDate,
} from "./entityLogic";
import {
  deleteEntityDocument,
  uploadEntityDocument,
  validateEntityDocument,
} from "../../services/backend/EntityService";

// Panel de documentos de la entidad. Lo que se le pide sale del catálogo de la
// configuración, filtrado por la naturaleza jurídica de ESTA entidad: a una
// alcaldía no se le pide cámara de comercio, así que ni siquiera aparece la
// casilla.
//
// "Vencido" no viene del servidor: se deriva aquí, contra la fecha de hoy, con
// la misma función que usa el backend. Un papel se vence solo el día que le
// toca, sin que nadie escriba nada.

const cap = (maxFiles) => (Number(maxFiles) === 0 ? Infinity : Math.max(1, Number(maxFiles) || 1));

export default function EntityDocuments({ entity, catalog, onChange }) {
  const user = useSelector((state) => state.user.value);
  const perms = user?.permissions || [];
  const isAdmin = (user?.roles || []).includes("ADMIN");
  // Two audiences: the internal manager, who uploads documents for any entity,
  // and the company itself from /my-entity, uploading its own. The backend forces
  // the company's NIT, so the permission is enough here.
  const canUpload =
    perms.includes(PERMISSIONS.ENTITY.UPDATE)
    || perms.includes(PERMISSIONS.MY_ENTITY.UPLOAD_DOC)
    || isAdmin;
  // Validating is NOT delegated: signing off a document is an institutional act,
  // and a company validating its own paperwork validates nothing.
  const canValidate = perms.includes(PERMISSIONS.ENTITY.VALIDATE_DOC) || isAdmin;

  const [busy, setBusy] = useState(null); // clave del documento en curso
  const [error, setError] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null); // { type, fileId }
  // { docDef, file, replaceFileId, expiryDate, issuedAt } — un documento puede
  // pedir la fecha de vencimiento, la de expedición o las dos.
  const [datesDialog, setDatesDialog] = useState(null);

  const today = isoToday();
  const docs = entity?.documents || {};

  // Sólo los documentos que le aplican a esta entidad.
  const applicable = useMemo(
    () => catalogForEntityType(catalog, entity?.entityType),
    [catalog, entity?.entityType]
  );

  const runAction = async (key, fn) => {
    setBusy(key);
    setError(null);
    const result = await fn();
    setBusy(null);
    if (!result.ok) {
      setError(
        ENTITY_REQUEST_ERROR_LABELS[result.error] ||
          "No fue posible completar la acción. Intenta de nuevo."
      );
      return false;
    }
    onChange(result.data);
    return true;
  };

  // Un documento con vigencia no se puede cargar sin decir hasta cuándo sirve,
  // y uno cuya vigencia se cuenta desde su expedición no se puede cargar sin esa
  // fecha, así que el archivo espera en un diálogo hasta que estén escritas.
  const handlePick = (docDef, file, replaceFileId) => {
    if (!file) return;
    if (docDef.hasExpiry || docDef.hasIssueDate) {
      setDatesDialog({ docDef, file, replaceFileId, expiryDate: "", issuedAt: "" });
      return;
    }
    runAction(docDef.key, () =>
      uploadEntityDocument({ nit: entity.nit, type: docDef.key, file, replaceFileId, user })
    );
  };

  const confirmDates = async () => {
    const { docDef, file, replaceFileId, expiryDate, issuedAt } = datesDialog;
    const ok = await runAction(docDef.key, () =>
      uploadEntityDocument({
        nit: entity.nit,
        type: docDef.key,
        file,
        expiryDate: expiryDate || null,
        issuedAt: issuedAt || null,
        replaceFileId,
        user,
      })
    );
    if (ok) setDatesDialog(null);
  };

  const confirmReject = async (comments) => {
    const { type, fileId } = rejectDialog;
    const ok = await runAction(type, () =>
      validateEntityDocument({
        nit: entity.nit,
        type,
        fileId,
        status: ENTITY_DOC_FILE_STATUS.REJECTED,
        comments,
        user,
      })
    );
    if (ok) setRejectDialog(null);
  };

  if (applicable.length === 0) {
    return (
      <Alert severity="info">
        La configuración no exige ningún documento para este tipo de entidad.
      </Alert>
    );
  }

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2}>
        {applicable.map((docDef) => {
          const files = docFiles(docs[docDef.key]);
          const room = files.length < cap(docDef.maxFiles);
          const loading = busy === docDef.key;

          return (
            <Grid item xs={12} md={6} key={docDef.key}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {docDef.label || docDef.key}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                        <Chip size="small" color={docDef.required === false ? "default" : "primary"}
                          label={docDef.required === false ? "Opcional" : "Obligatorio"} />
                        {docDef.hasExpiry && (
                          <Chip size="small" variant="outlined" label="Con vigencia" />
                        )}
                      </Stack>
                    </Box>
                    {canUpload && room && (
                      <Tooltip title="Cargar archivo">
                        <span>
                          <Button component="label" size="small" variant="outlined"
                            startIcon={<UploadFileIcon />} disabled={loading} sx={{ flexShrink: 0 }}>
                            Cargar
                            <input hidden type="file"
                              accept={(docDef.acceptedMime || []).join(",") || undefined}
                              onChange={(e) => {
                                handlePick(docDef, e.target.files?.[0], null);
                                // Permite volver a elegir el MISMO archivo: sin
                                // esto el change no se vuelve a disparar.
                                e.target.value = "";
                              }} />
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>

                  {docDef.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {docDef.description}
                    </Typography>
                  )}

                  {loading && <LinearProgress sx={{ mt: 1 }} />}

                  {files.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      Sin cargar.
                    </Typography>
                  ) : (
                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                      {files.map((file) => (
                        <FileRow
                          key={file.id}
                          file={file}
                          docDef={docDef}
                          today={today}
                          disabled={loading}
                          canUpload={canUpload}
                          canValidate={canValidate}
                          onReplace={(picked) => handlePick(docDef, picked, file.id)}
                          onValidate={() =>
                            runAction(docDef.key, () =>
                              validateEntityDocument({
                                nit: entity.nit,
                                type: docDef.key,
                                fileId: file.id,
                                status: ENTITY_DOC_FILE_STATUS.VALIDATED,
                                user,
                              })
                            )
                          }
                          onReject={() => setRejectDialog({ type: docDef.key, fileId: file.id })}
                          onDelete={() =>
                            window.confirm(`¿Quitar "${file.name}"?`) &&
                            runAction(docDef.key, () =>
                              deleteEntityDocument({
                                nit: entity.nit,
                                type: docDef.key,
                                fileId: file.id,
                                user,
                              })
                            )
                          }
                        />
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {datesDialog && (
        <DocumentDatesDialog
          docDef={datesDialog.docDef}
          fileName={datesDialog.file?.name}
          expiryDate={datesDialog.expiryDate}
          issuedAt={datesDialog.issuedAt}
          today={today}
          onChange={(patch) => setDatesDialog((d) => ({ ...d, ...patch }))}
          onCancel={() => setDatesDialog(null)}
          onConfirm={confirmDates}
          saving={busy === datesDialog.docDef.key}
        />
      )}

      {rejectDialog && (
        <RejectDialog
          onCancel={() => setRejectDialog(null)}
          onConfirm={confirmReject}
          saving={busy === rejectDialog.type}
        />
      )}
    </>
  );
}

function FileRow({ file, docDef, today, disabled, canUpload, canValidate, onReplace, onValidate, onReject, onDelete }) {
  const status = fileEffectiveStatus(file, today);
  const days = daysUntilExpiry(file, today);
  // Avisa antes de que se venza, con la anticipación que diga la configuración.
  const warnDays = Number(docDef.expiryWarningDays || 30);
  const expiringSoon = days !== null && days >= 0 && days <= warnDays;

  return (
    <Box sx={{ p: 1, borderRadius: 1, bgcolor: "action.hover" }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap title={file.name}>{file.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatBytes(file.size)}
            {file.version > 1 ? ` · v${file.version}` : ""}
            {file.uploadedAt ? ` · ${formatDateTime(file.uploadedAt)}` : ""}
          </Typography>
        </Box>
        <Chip size="small" color={DOC_FILE_STATUS_COLOR[status] || "default"}
          label={DOC_FILE_STATUS_LABELS[status] || status} sx={{ flexShrink: 0 }} />
      </Stack>

      {/* La expedición se muestra sin color de alarma: cuánto sirve desde esa
          fecha lo decide el módulo que la consuma, no este panel. */}
      {file.issuedAt && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Expedido el {formatDate(file.issuedAt)}
        </Typography>
      )}

      {file.expiryDate && (
        <Typography variant="caption"
          color={status === "EXPIRED" ? "error.main" : expiringSoon ? "warning.main" : "text.secondary"}
          display="block" sx={{ mt: 0.5 }}>
          Vigente hasta {formatDate(file.expiryDate)}
          {status === "EXPIRED"
            ? ` · vencido hace ${Math.abs(days)} día(s)`
            : expiringSoon ? ` · vence en ${days} día(s)` : ""}
        </Typography>
      )}

      {file.rejectionComments && (
        <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
          Rechazado: {file.rejectionComments}
        </Typography>
      )}

      {file.validatedByName && status === "VALIDATED" && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Validado por {file.validatedByName} · {formatDateTime(file.validatedAt)}
        </Typography>
      )}

      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
        {file.url && (
          <Tooltip title="Abrir">
            <IconButton size="small" component="a" href={file.url} target="_blank" rel="noopener noreferrer">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canValidate && status !== "VALIDATED" && (
          <Tooltip title="Validar">
            <span>
              <IconButton size="small" color="success" disabled={disabled} onClick={onValidate}>
                <CheckCircleOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {canValidate && status !== "REJECTED" && status !== "VALIDATED" && (
          <Tooltip title="Rechazar">
            <span>
              <IconButton size="small" color="error" disabled={disabled} onClick={onReject}>
                <HighlightOffIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {canUpload && (
          <Tooltip title="Reemplazar">
            <IconButton size="small" component="label" disabled={disabled}>
              <UploadFileIcon fontSize="small" />
              <input hidden type="file"
                accept={(docDef.acceptedMime || []).join(",") || undefined}
                onChange={(e) => { onReplace(e.target.files?.[0]); e.target.value = ""; }} />
            </IconButton>
          </Tooltip>
        )}
        {canUpload && (
          <Tooltip title="Quitar">
            <span>
              <IconButton size="small" disabled={disabled} onClick={onDelete}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

// Fechas del documento antes de cargarlo. Pide las que su tipo declare:
//
//   hasExpiry     → «Vigente hasta», OPCIONAL: la declara quien carga y hay
//                   ejemplares que no vencen.
//   hasIssueDate  → «Fecha de expedición», OBLIGATORIA: es de donde otro módulo
//                   deriva la vigencia (el RUT: 3 meses, en la solicitud de
//                   pago jurídica), así que sin ella el documento no se podría
//                   juzgar nunca. El servidor la exige igual; esto solo evita
//                   gastar un viaje.
//
// Un tipo puede declarar las dos y ninguna pisa a la otra.
function DocumentDatesDialog({
  docDef, fileName, expiryDate, issuedAt, today, onChange, onCancel, onConfirm, saving,
}) {
  // Misma regla que el backend, traída del espejo compartido en vez de
  // reimplementada: fecha de calendario y no futura.
  const issue = validateIssueDate(issuedAt, { required: !!docDef.hasIssueDate, today });
  const ISSUE_ERROR = {
    ISSUE_DATE_REQUIRED: "Indica la fecha de expedición del documento",
    INVALID_ISSUE_DATE: "Esa fecha no existe",
    ISSUE_DATE_IN_FUTURE: "La expedición no puede ser posterior a hoy",
  };
  // El vacío no se señala como error mientras nadie haya escrito nada: se
  // explica en el texto de ayuda y el botón queda deshabilitado.
  const issueError = issuedAt && !issue.valid ? ISSUE_ERROR[issue.errors.issuedAt] : "";

  return (
    <Dialog open onClose={saving ? undefined : onCancel} fullWidth maxWidth="xs">
      <DialogTitle>
        {docDef.hasIssueDate && !docDef.hasExpiry ? "Fecha del documento" : "Vigencia del documento"}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>{docDef.label}</strong>
          {fileName ? ` · ${fileName}` : ""}
        </Typography>
        <Stack spacing={2}>
          {docDef.hasIssueDate && (
            <TextField
              type="date"
              label="Fecha de expedición"
              value={issuedAt || ""}
              onChange={(e) => onChange({ issuedAt: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today }}
              error={!!issueError}
              helperText={issueError || "Cuándo se expidió el documento. Es obligatoria."}
              fullWidth
              autoFocus
            />
          )}
          {docDef.hasExpiry && (
            <TextField
              type="date"
              label="Vigente hasta"
              value={expiryDate || ""}
              onChange={(e) => onChange({ expiryDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Déjalo vacío si este ejemplar no vence."
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={onConfirm} disabled={saving || !issue.valid}>
          {saving ? "Cargando…" : "Cargar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RejectDialog({ onCancel, onConfirm, saving }) {
  const [comments, setComments] = useState("");
  // El servidor exige 5 caracteres: rechazar sin decir por qué deja a la entidad
  // adivinando qué corregir. Se refleja aquí para no gastar un viaje.
  const tooShort = comments.trim().length < 5;
  return (
    <Dialog open onClose={saving ? undefined : onCancel} fullWidth maxWidth="xs">
      <DialogTitle>Rechazar documento</DialogTitle>
      <DialogContent>
        <TextField
          label="Motivo del rechazo"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          autoFocus
          helperText="Explica qué debe corregir la entidad (mínimo 5 caracteres)."
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="error" disabled={saving || tooShort}
          onClick={() => onConfirm(comments.trim())}>
          {saving ? "Guardando…" : "Rechazar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
